// src/components/Chat/MessageList.jsx

import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Fab, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MessageBubble from './MessageBubble';
import { useUser } from '../../contexts/UserContext'; // ✅ userInfo 사용

// 날짜 포맷 함수
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
    });
};

const isMineById = (message, userInfo) => {
    // 1️⃣ 아직 userInfo.id를 모르면, 전부 내 메시지가 아닌 걸로 처리
    if (!userInfo || userInfo.id == null) {
        return false;
    }

    // 2️⃣ sender_id가 없으면 내 메시지라고 볼 수 없음
    if (message?.sender_id == null) {
        return false;
    }

    // 3️⃣ 진짜 id 비교
    return message.sender_id === userInfo.id;
};
const MessageList = ({ messages, containerRef, nickname }) => {
    const { userInfo } = useUser(); // ✅ userInfo 가져오기
    const prevLengthRef = useRef(messages.length);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [newCount, setNewCount] = useState(0);

    const scrollToBottom = useCallback(() => {
        const el = containerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [containerRef]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setIsAtBottom(atBottom);
            if (atBottom) setNewCount(0);
        };

        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [containerRef]);

    useEffect(() => {
        const prevLen = prevLengthRef.current;
        const currLen = messages.length;

        if (currLen > prevLen && !isAtBottom) {
            setNewCount((c) => c + (currLen - prevLen));
        } else if (isAtBottom) {
            scrollToBottom();
        }

        prevLengthRef.current = currLen;
    }, [messages, isAtBottom, scrollToBottom]);

    const handleJump = () => {
        scrollToBottom();
        setNewCount(0);
    };

    let lastDate = null;

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                ref={containerRef}
                sx={{
                    height: '60vh',
                    overflowY: 'auto',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {messages.map((m) => {
                    const createdAt = m.created_at || m.ts || '';
                    const messageDate = formatDate(createdAt);
                    const showDate = messageDate !== lastDate;
                    lastDate = messageDate;

                    const isMine = isMineById(m, userInfo);  // ✅ 여기 이렇게


                    return (
                        <Box key={`${m.id}-${isMine}`}>
                            {showDate && (
                                <Typography
                                    variant="caption"
                                    sx={{ textAlign: 'center', mb: 1, color: 'text.secondary' }}
                                >
                                    {messageDate}
                                </Typography>
                            )}
                            <MessageBubble message={m} isMine={isMine} />
                        </Box>
                    );
                })}
            </Box>

            {newCount > 0 && (
                <Fab
                    size="small"
                    onClick={handleJump}
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                    }}
                >
                    <ArrowDownwardIcon />
                </Fab>
            )}
        </Box>
    );
};

export default MessageList;
